import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Boxes, PackagePlus, Loader2, Trash2, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "./api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const today = () => new Date().toISOString().slice(0, 10);

const Summary = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-[#0C1322] border border-white/10 p-5" data-testid={testid}>
    <div className="flex items-center justify-between"><Icon size={18} style={{ color: accent }} /><span className="mono-label" style={{ color: "#64748B" }}>{label}</span></div>
    <div className="mt-4 font-display font-black text-3xl text-white">{value}</div>
  </div>
);

export default function Inventory() {
  const [data, setData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [form, setForm] = useState({ code: "", qty: "", note: "", date: today() });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inv, meta, rec] = await Promise.all([
        api.get("/inventory"),
        api.get("/inventory/meta"),
        api.get("/inventory/receipts"),
      ]);
      setData(inv.data);
      setMaterials(meta.data.materials);
      setReceipts(rec.data);
    } catch {
      toast.error("Failed to load inventory");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const receive = async (e) => {
    e.preventDefault();
    if (!form.code) { toast.error("Select a material"); return; }
    if (!form.qty || parseFloat(form.qty) <= 0) { toast.error("Enter a quantity"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/receipts", { ...form, qty: parseFloat(form.qty) });
      toast.success("Stock received");
      setForm({ code: "", qty: "", note: "", date: today() });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/inventory/receipts/${deleteTarget.id}`);
      toast.success("Receipt removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onHandColor = (v) => (v <= 0 ? "#ef4444" : v < 10 ? "#f59e0b" : "#22c55e");

  return (
    <div data-testid="inventory-page">
      <span className="mono-label" style={{ color: "#00F0FF" }}>Material Stock</span>
      <h1 className="mt-2 mb-6 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight flex items-center gap-3">
        <Boxes size={30} style={{ color: "#00F0FF" }} /> Inventory
      </h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Summary icon={TrendingUp} label="Total Received" value={data ? data.total_received : "—"} accent="#22c55e" testid="inv-received" />
        <Summary icon={TrendingDown} label="Total Consumed" value={data ? data.total_consumed : "—"} accent="#00F0FF" testid="inv-consumed" />
        <Summary icon={AlertTriangle} label="Low / Out of Stock" value={data ? data.low_stock_count : "—"} accent="#f59e0b" testid="inv-low" />
      </div>

      {/* Stock table */}
      <div className="bg-[#0C1322] border border-white/10 overflow-x-auto mb-8" data-testid="inventory-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left" style={{ color: "#64748B" }}>
              <th className="p-4 mono-label font-normal">Material</th>
              <th className="p-4 mono-label font-normal text-right">Received</th>
              <th className="p-4 mono-label font-normal text-right">Consumed</th>
              <th className="p-4 mono-label font-normal text-right">On Hand</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((i) => (
              <tr key={i.code} className="border-b border-white/5 hover:bg-[#131C31] transition-colors" data-testid={`inv-row-${i.code}`}>
                <td className="p-4 text-white">{i.name}<span className="block mono-label text-[10px] mt-0.5" style={{ color: "#64748B" }}>{i.group} · {i.unit}</span></td>
                <td className="p-4 text-right text-slate-300">{i.received}</td>
                <td className="p-4 text-right text-slate-300">{i.consumed}</td>
                <td className="p-4 text-right font-display font-bold text-lg" style={{ color: onHandColor(i.on_hand) }}>{i.on_hand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Receive stock */}
      <div className="bg-[#0C1322] border border-white/10 p-6 mb-8">
        <div className="flex items-center gap-2 mb-5"><PackagePlus size={18} style={{ color: "#00F0FF" }} /><h2 className="font-display font-bold text-lg text-white">Receive Stock</h2></div>
        <form onSubmit={receive} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="receive-form">
          <div>
            <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Material *</label>
            <select className="field appearance-none cursor-pointer" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="receive-material">
              <option value="">Select material</option>
              {materials.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Quantity *</label>
            <input type="number" min="0" step="0.5" className="field" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="receive-qty" />
          </div>
          <div>
            <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Date</label>
            <input type="date" className="field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Note</label>
            <input className="field" placeholder="e.g. PO#1234 / vendor" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="lg:col-span-4">
            <button type="submit" disabled={saving} data-testid="receive-submit"
              className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3 mono-label transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <><PackagePlus size={16} /> Add to Stock</>}
            </button>
          </div>
        </form>
      </div>

      {/* Receipts history */}
      <h2 className="font-display font-bold text-lg text-white mb-4">Stock Receipts</h2>
      <div className="bg-[#0C1322] border border-white/10 overflow-x-auto">
        {receipts.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "#64748B" }} data-testid="receipts-empty">No stock receipts recorded yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead>
              <tr className="border-b border-white/10 text-left" style={{ color: "#64748B" }}>
                <th className="p-4 mono-label font-normal">Date</th>
                <th className="p-4 mono-label font-normal">Material</th>
                <th className="p-4 mono-label font-normal text-right">Qty</th>
                <th className="p-4 mono-label font-normal">Note</th>
                <th className="p-4 mono-label font-normal">By</th>
                <th className="p-4 mono-label font-normal text-right"></th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-[#131C31] transition-colors" data-testid={`receipt-row-${r.id}`}>
                  <td className="p-4 text-white">{r.date}</td>
                  <td className="p-4 text-slate-300">{r.material_name}</td>
                  <td className="p-4 text-right text-cyan font-medium" style={{ color: "#00F0FF" }}>+{r.qty}</td>
                  <td className="p-4 text-slate-400">{r.note || "—"}</td>
                  <td className="p-4 text-slate-400">{r.created_by || "—"}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setDeleteTarget(r)} data-testid={`delete-receipt-${r.id}`} className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0C1322] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove stock receipt?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#94A3B8" }}>
              This removes {deleteTarget?.qty} × {deleteTarget?.material_name} from received stock and will reduce On Hand accordingly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-receipt" className="bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
